import { valueNotice } from "../shared/value-copy";
import { useEffect, useState } from "react";
import {
  Alert,
  Card,
  Grid,
  KpiCard,
  Panel,
  SegmentedControl,
  Stack,
  StatusBadge
} from "@leylion/design-system";
import {
  getBranchComparison,
  getCashPaymentsAnalytics,
  getCustomerAnalytics,
  getEmployeeAnalytics,
  getExecutiveDashboard,
  getForecast,
  getInventoryAnalytics,
  getProductAnalytics,
  getProfitabilityAnalytics,
  getPurchasingAnalytics,
  getRestaurantAnalytics,
  getSalesAnalytics,
  getTaxVatReport
} from "../api/client";
import type {
  BranchComparison,
  CashPaymentsAnalytics,
  CustomerAnalytics,
  EmployeeAnalytics,
  ExecutiveDashboard,
  Forecast,
  InventoryAnalytics,
  ProductAnalytics,
  ProfitabilityAnalytics,
  PurchasingAnalytics,
  ReportScope,
  RestaurantAnalytics,
  SalesAnalytics,
  TaxVatReport
} from "../api/reports-types";
import {
  BreakdownBars,
  DynamicReportTable,
  KpiGrid,
  LoadingReport,
  ReportError,
  SectionHeading,
  ThreeColumn,
  TrendChart,
  TwoColumn,
  formatMetric,
  money,
  quantity,
  type ReportsLanguage
} from "./reports-components";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected error.";
}

function useReportData<T>(
  key: string,
  loader: () => Promise<T>
): {
  data: T | null;
  error: string;
  busy: boolean;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let active = true;
    setBusy(true);
    setError("");
    loader()
      .then((value) => {
        if (active) setData(value);
      })
      .catch((caught) => {
        if (active) setError(errorMessage(caught));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [key]);

  return { data, error, busy };
}

export function ExecutiveView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData(
    `executive:${JSON.stringify(scope)}:${refreshKey}`,
    () => getExecutiveDashboard(scope)
  );

  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }

  const data = state.data;
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={data.kpis}
        currency={scope.currency}
      />
      <Alert
        tone="info"
        title={ar ? "الملخص التنفيذي" : "Executive brief"}
      >
        {ar ? data.executiveBriefAr ?? data.executiveBrief : data.executiveBrief}
      </Alert>
      <TrendChart
        language={language}
        title={ar ? "اتجاه الإيراد" : "Revenue trend"}
        series={data.revenueTrend}
        currency={scope.currency}
      />
      <TwoColumn>
        <BreakdownBars
          language={language}
          title={ar ? "مزيج الإيرادات" : "Revenue mix"}
          rows={data.revenueMix}
          currency={scope.currency}
        />
        <BreakdownBars
          language={language}
          title={ar ? "الفروع الأعلى" : "Top branches"}
          rows={data.topBranches}
          currency={scope.currency}
        />
      </TwoColumn>
      <Panel title={ar ? "تنبيهات الإدارة" : "Management alerts"}>
        <div className="reports-alert-list">
          {data.managementAlerts.map((alert) => (
            <div key={alert} className="reports-alert-item">
              <StatusBadge
                tone={alert.toLowerCase().includes("no critical")
                  ? "success"
                  : "warning"}
              >
                {ar ? "متابعة" : "Review"}
              </StatusBadge>
              <span>{valueNotice(alert, language)}</span>
            </div>
          ))}
        </div>
      </Panel>
    </Stack>
  );
}

export function SalesView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<SalesAnalytics>(
    `sales:${JSON.stringify(scope)}:${refreshKey}`,
    () => getSalesAnalytics(scope)
  );

  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }

  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <TrendChart
        language={language}
        title={ar ? "المبيعات اليومية" : "Daily sales"}
        series={state.data.dailySales}
        currency={scope.currency}
      />
      <TwoColumn>
        <BreakdownBars
          language={language}
          title={ar ? "حسب نوع الخدمة" : "By service type"}
          rows={state.data.serviceTypeMix}
          currency={scope.currency}
        />
        <BreakdownBars
          language={language}
          title={ar ? "حسب طريقة الدفع" : "By payment method"}
          rows={state.data.paymentMix}
          currency={scope.currency}
        />
      </TwoColumn>
      <DynamicReportTable
        language={language}
        title={ar ? "تفاصيل المبيعات" : "Sales detail"}
        table={state.data.detail}
        currency={scope.currency}
      />
    </Stack>
  );
}

export function ProfitabilityView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<ProfitabilityAnalytics>(
    `profitability:${JSON.stringify(scope)}:${refreshKey}`,
    () => getProfitabilityAnalytics(scope)
  );

  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }

  const data = state.data;
  const waterfall = [
    {
      label: ar ? "الإيراد" : "Revenue",
      value: data.revenueMinor,
      tone: "positive"
    },
    {
      label: ar ? "الخصم" : "Discount",
      value: -data.discountMinor,
      tone: "negative"
    },
    {
      label: ar ? "المرتجعات" : "Refunds",
      value: -data.refundMinor,
      tone: "negative"
    },
    {
      label: ar ? "تكلفة المبيعات" : "COGS",
      value: -data.costOfGoodsSoldMinor,
      tone: "negative"
    },
    {
      label: ar ? "المصروفات" : "Expenses",
      value: -data.operatingExpenseMinor,
      tone: "negative"
    },
    {
      label: ar ? "صافي الربح" : "Net profit",
      value: data.netProfitMinor,
      tone: data.netProfitMinor >= 0 ? "positive" : "negative"
    }
  ];
  const max = Math.max(...waterfall.map((row) => Math.abs(row.value)), 1);

  return (
    <Stack>
      <Grid columns={4}>
        <KpiCard
          label={ar ? "صافي المبيعات" : "Net sales"}
          value={money(data.netSalesMinor, language, scope.currency)}
        />
        <KpiCard
          label={ar ? "الربح الإجمالي" : "Gross profit"}
          value={money(data.grossProfitMinor, language, scope.currency)}
          trend={`${quantity(data.grossMarginPercent, language)}%`}
          trendTone={data.grossMarginPercent >= 25 ? "success" : "warning"}
        />
        <KpiCard
          label={ar ? "المصروفات" : "Operating expenses"}
          value={money(data.operatingExpenseMinor, language, scope.currency)}
        />
        <KpiCard
          label={ar ? "صافي الربح" : "Net profit"}
          value={money(data.netProfitMinor, language, scope.currency)}
          trend={`${quantity(data.netMarginPercent, language)}%`}
          trendTone={data.netProfitMinor >= 0 ? "success" : "danger"}
        />
      </Grid>
      <Panel
        title={ar ? "جسر الربحية" : "Profitability bridge"}
        description={
          ar
            ? "من الإيراد الإجمالي إلى صافي الربح."
            : "From gross revenue to net profit."
        }
      >
        <div className="reports-waterfall">
          {waterfall.map((row) => (
            <div key={row.label} className="reports-waterfall__item">
              <strong>{row.label}</strong>
              <div className="reports-waterfall__bar">
                <span
                  className={`is-${row.tone}`}
                  style={{
                    blockSize: `${Math.max(
                      8,
                      Math.abs(row.value) / max * 150
                    )}px`
                  }}
                />
              </div>
              <b>{money(row.value, language, scope.currency)}</b>
            </div>
          ))}
        </div>
      </Panel>
      <TrendChart
        language={language}
        title={ar ? "اتجاه الربح" : "Profit trend"}
        series={data.profitTrend}
        currency={scope.currency}
      />
      <BreakdownBars
        language={language}
        title={ar ? "هوامش الفئات" : "Category margins"}
        rows={data.categoryMargins}
        valueFormat="money"
        currency={scope.currency}
      />
    </Stack>
  );
}

type OperationsSection = "products" | "inventory" | "purchasing";

export function OperationsAnalyticsView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const [section, setSection] = useState<OperationsSection>("products");

  return (
    <Stack>
      <SegmentedControl
        value={section}
        onChange={setSection}
        label={ar ? "تقارير التشغيل" : "Operations reports"}
        options={[
          {
            value: "products",
            label: ar ? "المنتجات" : "Products"
          },
          {
            value: "inventory",
            label: ar ? "المخزون" : "Inventory"
          },
          {
            value: "purchasing",
            label: ar ? "المشتريات" : "Purchasing"
          }
        ]}
      />
      {section === "products" ? (
        <ProductsSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : null}
      {section === "inventory" ? (
        <InventorySection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : null}
      {section === "purchasing" ? (
        <PurchasingSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : null}
    </Stack>
  );
}

function ProductsSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<ProductAnalytics>(
    `products:${JSON.stringify(scope)}:${refreshKey}`,
    () => getProductAnalytics(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <TwoColumn>
        <BreakdownBars
          language={language}
          title={ar ? "أفضل المنتجات" : "Top products"}
          rows={state.data.topProducts}
          currency={scope.currency}
        />
        <BreakdownBars
          language={language}
          title={ar ? "المنتجات البطيئة" : "Slow products"}
          rows={state.data.slowProducts}
          valueFormat="number"
        />
      </TwoColumn>
      <DynamicReportTable
        language={language}
        title={ar ? "مصفوفة أداء المنتجات" : "Product performance matrix"}
        table={state.data.products}
        currency={scope.currency}
      />
    </Stack>
  );
}

function InventorySection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<InventoryAnalytics>(
    `inventory:${JSON.stringify(scope)}:${refreshKey}`,
    () => getInventoryAnalytics(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <TwoColumn>
        <BreakdownBars
          language={language}
          title={ar ? "قيمة المستودعات" : "Warehouse value"}
          rows={state.data.warehouseValues}
          currency={scope.currency}
        />
        <BreakdownBars
          language={language}
          title={ar ? "مخاطر التوريد" : "Stock risks"}
          rows={state.data.riskItems}
          valueFormat="number"
        />
      </TwoColumn>
      <DynamicReportTable
        language={language}
        title={ar ? "تغطية المخزون" : "Stock coverage"}
        table={state.data.stockCoverage}
        currency={scope.currency}
      />
    </Stack>
  );
}

function PurchasingSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<PurchasingAnalytics>(
    `purchasing:${JSON.stringify(scope)}:${refreshKey}`,
    () => getPurchasingAnalytics(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <TrendChart
        language={language}
        title={ar ? "اتجاه المشتريات" : "Purchase spend trend"}
        series={state.data.spendTrend}
        currency={scope.currency}
      />
      <BreakdownBars
        language={language}
        title={ar ? "أداء الموردين" : "Supplier performance"}
        rows={state.data.supplierPerformance}
        currency={scope.currency}
      />
      <DynamicReportTable
        language={language}
        title={ar ? "أوامر الشراء" : "Purchase orders"}
        table={state.data.purchaseOrders}
        currency={scope.currency}
      />
    </Stack>
  );
}

type PeopleSection = "customers" | "employees";

export function PeopleAnalyticsView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const [section, setSection] = useState<PeopleSection>("customers");
  return (
    <Stack>
      <SegmentedControl
        value={section}
        onChange={setSection}
        label={ar ? "العملاء والموظفون" : "Customers and employees"}
        options={[
          {
            value: "customers",
            label: ar ? "العملاء" : "Customers"
          },
          {
            value: "employees",
            label: ar ? "الموظفون" : "Employees"
          }
        ]}
      />
      {section === "customers" ? (
        <CustomerSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : (
        <EmployeeSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      )}
    </Stack>
  );
}

function CustomerSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<CustomerAnalytics>(
    `customers:${JSON.stringify(scope)}:${refreshKey}`,
    () => getCustomerAnalytics(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <TrendChart
        language={language}
        title={ar ? "العملاء الجدد" : "New customers"}
        series={state.data.newCustomerTrend}
        valueFormat="number"
      />
      <TwoColumn>
        <BreakdownBars
          language={language}
          title={ar ? "أداء الشرائح" : "Segment performance"}
          rows={state.data.segmentPerformance}
          valueFormat="number"
        />
        <BreakdownBars
          language={language}
          title={ar ? "أداء فئات الولاء" : "Loyalty tiers"}
          rows={state.data.loyaltyTierPerformance}
          valueFormat="number"
        />
      </TwoColumn>
    </Stack>
  );
}

function EmployeeSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<EmployeeAnalytics>(
    `employees:${JSON.stringify(scope)}:${refreshKey}`,
    () => getEmployeeAnalytics(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <DynamicReportTable
        language={language}
        title={ar ? "ترتيب أداء الموظفين" : "Employee leaderboard"}
        table={state.data.leaderboard}
        currency={scope.currency}
      />
    </Stack>
  );
}

export function RestaurantAnalyticsView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<RestaurantAnalytics>(
    `restaurant:${JSON.stringify(scope)}:${refreshKey}`,
    () => getRestaurantAnalytics(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <TwoColumn>
        <BreakdownBars
          language={language}
          title={ar ? "فترات الخدمة" : "Service periods"}
          rows={state.data.servicePeriods}
          valueFormat="number"
        />
        <BreakdownBars
          language={language}
          title={ar ? "محطات المطبخ" : "Kitchen stations"}
          rows={state.data.kitchenStations}
          valueFormat="number"
        />
      </TwoColumn>
      <DynamicReportTable
        language={language}
        title={ar ? "أداء التوصيل" : "Delivery performance"}
        table={state.data.deliveryPerformance}
        currency={scope.currency}
      />
    </Stack>
  );
}

type ComplianceSection = "vat" | "payments";

export function ComplianceAnalyticsView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const [section, setSection] = useState<ComplianceSection>("vat");
  return (
    <Stack>
      <SegmentedControl
        value={section}
        onChange={setSection}
        label={ar ? "الضريبة والمدفوعات" : "VAT and payments"}
        options={[
          {
            value: "vat",
            label: ar ? "ضريبة القيمة المضافة" : "VAT"
          },
          {
            value: "payments",
            label: ar ? "النقد والمدفوعات" : "Cash & Payments"
          }
        ]}
      />
      {section === "vat" ? (
        <VatSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : (
        <PaymentsSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      )}
    </Stack>
  );
}

function VatSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<TaxVatReport>(
    `vat:${JSON.stringify(scope)}:${refreshKey}`,
    () => getTaxVatReport(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  const data = state.data;
  return (
    <Stack>
      <Grid columns={4}>
        <KpiCard
          label={ar ? "مبيعات خاضعة" : "Taxable sales"}
          value={money(data.taxableSalesMinor, language, scope.currency)}
        />
        <KpiCard
          label={ar ? "ضريبة المخرجات" : "Output VAT"}
          value={money(data.outputTaxMinor, language, scope.currency)}
        />
        <KpiCard
          label={ar ? "ضريبة المدخلات" : "Input VAT"}
          value={money(data.recoverableInputTaxMinor, language, scope.currency)}
        />
        <KpiCard
          label={ar ? "صافي المستحق" : "Net VAT payable"}
          value={money(data.netTaxPayableMinor, language, scope.currency)}
          trendTone={data.netTaxPayableMinor >= 0 ? "warning" : "success"}
        />
      </Grid>
      <Panel title={ar ? "ملخص ضريبة القيمة المضافة" : "VAT summary"}>
        <div className="reports-dynamic-table-wrap">
          <table className="reports-dynamic-table">
            <thead>
              <tr>
                <th>{ar ? "الرمز" : "Code"}</th>
                <th>{ar ? "الوصف" : "Description"}</th>
                <th>{ar ? "الصافي" : "Net"}</th>
                <th>{ar ? "الضريبة" : "Tax"}</th>
                <th>{ar ? "المستحق" : "Payable"}</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((line) => (
                <tr key={line.code}>
                  <td>{line.code}</td>
                  <td>{ar ? line.descriptionAr : line.descriptionEn}</td>
                  <td>{money(line.netMinor, language, scope.currency)}</td>
                  <td>{money(line.taxMinor, language, scope.currency)}</td>
                  <td>{money(line.payableMinor, language, scope.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </Stack>
  );
}

function PaymentsSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<CashPaymentsAnalytics>(
    `cash:${JSON.stringify(scope)}:${refreshKey}`,
    () => getCashPaymentsAnalytics(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <BreakdownBars
        language={language}
        title={ar ? "مزيج طرق الدفع" : "Payment method mix"}
        rows={state.data.paymentMethods}
        currency={scope.currency}
      />
      <DynamicReportTable
        language={language}
        title={ar ? "تسويات الورديات" : "Shift reconciliations"}
        table={state.data.shiftReconciliations}
        currency={scope.currency}
      />
    </Stack>
  );
}

type StrategySection = "branches" | "forecast";

export function StrategyAnalyticsView({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const [section, setSection] = useState<StrategySection>("branches");
  return (
    <Stack>
      <SegmentedControl
        value={section}
        onChange={setSection}
        label={ar ? "الاستراتيجية" : "Strategy"}
        options={[
          {
            value: "branches",
            label: ar ? "مقارنة الفروع" : "Branch comparison"
          },
          {
            value: "forecast",
            label: ar ? "التوقعات" : "Forecast"
          }
        ]}
      />
      {section === "branches" ? (
        <BranchesSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      ) : (
        <ForecastSection
          language={language}
          scope={scope}
          refreshKey={refreshKey}
        />
      )}
    </Stack>
  );
}

function BranchesSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<BranchComparison>(
    `branches:${JSON.stringify(scope)}:${refreshKey}`,
    () => getBranchComparison(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }
  return (
    <Stack>
      <KpiGrid
        language={language}
        kpis={state.data.kpis}
        currency={scope.currency}
      />
      <DynamicReportTable
        language={language}
        title={ar ? "مصفوفة مقارنة الفروع" : "Branch comparison matrix"}
        table={state.data.branches}
        currency={scope.currency}
      />
    </Stack>
  );
}

function ForecastSection({
  language,
  scope,
  refreshKey
}: {
  language: ReportsLanguage;
  scope: ReportScope;
  refreshKey: number;
}) {
  const ar = language === "ar";
  const state = useReportData<Forecast>(
    `forecast:${JSON.stringify(scope)}:${refreshKey}`,
    () => getForecast(scope)
  );
  if (state.busy || !state.data) {
    return state.error
      ? <ReportError language={language} error={state.error} />
      : <LoadingReport language={language} />;
  }

  const data = state.data;
  return (
    <Stack>
      <Grid columns={3}>
        <KpiCard
          label={ar ? "التوقع القادم" : "Next forecast"}
          value={money(data.nextPeriodForecastMinor, language, scope.currency)}
        />
        <KpiCard
          label={ar ? "دقة النموذج" : "Model accuracy"}
          value={`${quantity(data.modelAccuracyPercent, language)}%`}
          trendTone={data.modelAccuracyPercent >= 80 ? "success" : "warning"}
        />
        <KpiCard
          label={ar ? "نطاق الثقة" : "Confidence range"}
          value={`±${quantity(data.confidenceRangePercent, language)}%`}
          trendTone={data.confidenceRangePercent <= 20 ? "success" : "warning"}
        />
      </Grid>
      <TrendChart
        language={language}
        title={ar ? "الفعلي والتوقع" : "Actual and forecast"}
        series={data.actualAndForecast}
        currency={scope.currency}
      />
      <ThreeColumn>
        {data.scenarios.map((scenario) => (
          <Card
            key={scenario.key}
            className={`reports-scenario reports-scenario--${scenario.key}`}
          >
            <span>{ar ? scenario.labelAr : scenario.labelEn}</span>
            <strong>
              {money(scenario.forecastMinor, language, scope.currency)}
            </strong>
            <small>
              {quantity(scenario.probabilityPercent, language)}%
              {" "}
              {ar ? "احتمال" : "probability"}
            </small>
          </Card>
        ))}
      </ThreeColumn>
      <Alert
        tone={data.risks.some((risk) =>
          risk.toLowerCase().includes("no material"))
          ? "success"
          : "warning"}
        title={ar ? "مخاطر التوقع" : "Forecast risks"}
      >
        {data.risks.join(" · ")}
      </Alert>
    </Stack>
  );
}
